import {
  useState,
  useEffect,
  useContext,
  type ChangeEvent,
  type MouseEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  Paper,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  Box,
  Dialog,
  IconButton,
} from "@mui/material";
import {
  DeleteForeverOutlined as DeleteForeverOutlinedIcon,
  Close as CloseIcon,
  QuestionMark,
} from "@mui/icons-material";
import JoinDisasterSelect from "./JoinDisasterSelect";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../../../components/SnackBarCustom";
import PopoverCustom from "../../../../../components/PopoverCustom";
import { formatDate } from "../../../../../utils/deteUtils";
import { ManualCreateDisasterRole } from "../../../../../role";
import { TokenContext } from "../../../../../TokenContext";
import {
  getDisasterDetails,
  joinDisaster,
  splitDisaster,
  deleteDisaster,
} from "../../../../../api";
import type {
  DisasterDetailItem,
  DisasterDetailStatusEntry,
  DisasterSelectValue,
  DisasterStatusItem,
} from "../../../../../types/disaster";

type DetailColumnId =
  | "CheckBox"
  | "condition_kind"
  | "description"
  | "ss_count"
  | "last_detect_at";

type DetailColumn = {
  id: DetailColumnId;
  label: string;
  minWidth: string;
  align?: "left" | "right" | "center";
  CheckBox?: boolean;
  isSmallScreen: boolean;
  checkBox?: boolean;
  site?: boolean;
  kind?: boolean;
  count?: boolean;
  date?: boolean;
};

type CheckedDetailEntry = Record<number, number[]>;

const columns: DetailColumn[] = [
  {
    id: "CheckBox",
    label: "",
    minWidth: "64px",
    CheckBox: true,
    isSmallScreen: true,
    checkBox: true,
  },
  {
    id: "condition_kind",
    label: "種類",
    minWidth: "112px",
    isSmallScreen: true,
    site: true,
    kind: true,
  },
  {
    id: "description",
    label: "内容",
    minWidth: "96px",
    isSmallScreen: true,
    site: true,
  },
  {
    id: "ss_count",
    label: "被災SS数",
    minWidth: "144px",
    isSmallScreen: false,
    site: true,
    count: true,
  },
  {
    id: "last_detect_at",
    label: "検知日時",
    minWidth: "96px",
    isSmallScreen: false,
    site: true,
    date: true,
  },
];

const DisasterInfomation = () => {
  const navigate = useNavigate();
  const tokenContext = useContext(TokenContext);
  const user = tokenContext?.user;
  const manualCreateDisasterRole = ManualCreateDisasterRole(
    user?.m_organization.m_role_id ?? 0
  );
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [disasterDetails, setDisasterDetails] = useState<DisasterDetailItem[]>(
    []
  );
  const [totalData, setTotalData] = useState(0);

  const fetchDisasterDetails = async () => {
    if (!disasterId) {
      return;
    }
    try {
      const data = await getDisasterDetails(disasterId, {
        order: "last_detect_at",
        sort: "desc",
        page: page + 1,
        limit: rowsPerPage,
      });
      if (data.status === "ng") {
        navigate("/dashboard");
      } else {
        setDisasterDetails(data.disaster_details);
        setTotalData(data.total_count ?? 0);
        setSnackBarstatus(data);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("API呼び出しエラー", error);
      navigate("/dashboard");
    }
  };

  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    fetchDisasterDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  const [selectedRow, setSelectedRow] = useState<DisasterDetailItem | null>(
    null
  );
  //*ポップアップ設定 */
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  //チェックボックスのチェック管理
  const [checkedDetails, setCheckedDetails] = useState<CheckedDetailEntry[]>(
    []
  );
  const [checkedName, setCheckedName] = useState<string[]>([]);

  type CheckboxValidateProps = {
    siteId: number;
    sites: DisasterDetailStatusEntry[];
    name?: string;
  };

  const CheckboxValidate = ({
    siteId,
    sites,
    name,
  }: CheckboxValidateProps) => {
    const site = sites
      .map((entry) => entry.site_id)
      .filter((id): id is number => id != null);
    const isChecked = checkedDetails.some((item) => item[siteId] != null);
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setCheckedDetails((prevChecked) => {
          if (!prevChecked.some((item) => item[siteId] != null)) {
            return [...prevChecked, { [siteId]: site }];
          }
          return prevChecked;
        });
        if (name) {
          setCheckedName((prevChecked) =>
            prevChecked.includes(name) ? prevChecked : [...prevChecked, name]
          );
        }
      } else {
        setCheckedDetails((prevChecked) =>
          prevChecked.filter((checkedItem) => checkedItem[siteId] == null)
        );
        if (name) {
          setCheckedName((prevChecked) =>
            prevChecked.filter((checkedId) => checkedId !== name)
          );
        }
      }
    };

    return (
      <Checkbox
        size="small"
        checked={isChecked}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleChange(e)}
        sx={{ cursor: "pointer", pointerEvents: "auto" }}
      />
    );
  };

  const disasterValidate = (disasterName: string | undefined) => {
    return (
      <Box
        sx={{
          borderRadius: "20px",
          padding: "8px",
          color: "white",
          fontWeight: "bold",
          width: "fit-content",
          backgroundColor:
            disasterName === "quake"
              ? "red"
              : disasterName === "w-r-s"
              ? "orange"
              : disasterName === "manual"
              ? "gray"
              : "black",
        }}
      >
        {disasterName === "quake"
          ? "地震"
          : disasterName === "w-r-s"
          ? "雨風雪"
          : disasterName === "manual"
          ? "手動"
          : disasterName === "alert"
          ? "警報"
          : "その他"}
      </Box>
    );
  };

  const handleLocationCodeClick = (row: DisasterDetailItem) => {
    setSelectedRow(row);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  //検索結果ページネーション
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //選択した災害名称管理
  const [DisasterName, setDisasterName] = useState<string>();
  const handleChangeDisaster = (e: DisasterSelectValue) => {
    setSelectedContents((prev) => ({
      ...prev,
      dst_id: e.id ?? null,
    }));
    setDisasterName(e.name);
  };

  const total = checkedDetails.reduce((sum, item) => {
    const keys = Object.keys(item);
    const values = item[Number(keys[0])];
    return sum + (values?.length ?? 0);
  }, 0);

  const allSites = checkedDetails.flatMap((item) => {
    const key = Object.keys(item)[0];
    return item[Number(key)] ?? [];
  });

  //結合確認コメント
  const joinMessage = (
    <div>
      災害情報
      <br />
      {checkedName.map((name, index) => (
        <span key={index}>
          ・{name}
          <br />
        </span>
      ))}
      に関連するSS {total}件を
      <br />"{DisasterName}"に結合しますか？
    </div>
  );

  //分割確認コメント
  const splitMessage = (
    <div>
      災害情報
      <br />
      {checkedName.map((name, index) => (
        <span key={index}>
          ・{name}
          <br />
        </span>
      ))}
      に関連するSS {total}件を分割しますか？
    </div>
  );

  //削除確認コメント
  const deleteMessage = (
    <div>
      災害情報
      <br />
      {checkedName.map((name, index) => (
        <span key={index}>
          ・{name}
          <br />
        </span>
      ))}
      に関連するSS {total}件を削除しますか？
    </div>
  );

  //災害情報結合
  const [popoverJoinOpen, setPopoverJoinOpen] = useState(false);
  const joinButtonClick = () => {
    setPopoverJoinOpen(true);
  };
  const joinPopupClose = () => {
    setPopoverJoinOpen(false);
  };
  const [selectedContents, setSelectedContents] = useState<{
    dst_id: number | null;
  }>({
    dst_id: null,
  });
  const joinDisasters = async (isSuccess: boolean) => {
    if (!isSuccess || !disasterId) {
      return;
    }

    const params = {
      site_id: allSites,
      dst_id: selectedContents.dst_id ?? 0,
    };
    console.log("災害情報結合入力データ", params);
    try {
      const status = await joinDisaster(disasterId, params);
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      console.log("災害情報結合:", status);
      //結合後選択済みサイトをリセット
      setCheckedDetails([]);
      setCheckedName([]);
      fetchDisasterDetails();
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  };

  //災害情報分割
  const [popoverSplitOpen, setPopoverSplitOpen] = useState(false);
  const splitButtonClick = () => {
    setPopoverSplitOpen(true);
  };
  const splitPopupClose = () => {
    setPopoverSplitOpen(false);
  };
  const splitDisasters = async (isSuccess: boolean) => {
    if (!isSuccess || !disasterId) {
      return;
    }

    const params = {
      site_id: allSites,
    };
    try {
      const data = await splitDisaster(disasterId, params);
      setSnackBarstatus(data);
      setSnackbarOpen(true);
      //結合後選択済みサイトをリセット
      setCheckedDetails([]);
      setCheckedName([]);
      fetchDisasterDetails();
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  };

  //災害情報削除
  const [popoverDeleteOpen, setPopoverDeleteOpen] = useState(false);
  const deleteButtonClick = () => {
    setPopoverDeleteOpen(true);
  };
  const deletePopupClose = () => {
    setPopoverDeleteOpen(false);
  };

  const deleteDisasters = async (isSuccess: boolean) => {
    if (!isSuccess || !disasterId) {
      return;
    }

    try {
      const status = await deleteDisaster(disasterId, { site_id: allSites });
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      //結合後選択済みサイトをリセット
      setCheckedDetails([]);
      setCheckedName([]);
      fetchDisasterDetails();
    } catch (error) {
      console.error("エラーが発生しました:", error);
    }
  };

  // 対象SS確認ボタン
  const [ssConfirmDisasterStatus, setSsConfirmDisasterStatus] = useState<
    DisasterDetailStatusEntry[]
  >([]);
  const ssConfirmMessage = (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell key="code">Sコード</TableCell>
            <TableCell key="name">SS名称</TableCell>
            <TableCell key="operator">運営店</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ssConfirmDisasterStatus.map((ds) => (
            <TableRow key={ds.id}>
              <TableCell>{ds.site?.code}</TableCell>
              <TableCell>{ds.site?.name}</TableCell>
              <TableCell>{ds.site?.operator}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
  // 災害名登録ポップオーバー管理
  const [ssConfirmOpen, setSsConfirmOpen] = useState(false);
  const ssConfirmBtnClick = (
    e: MouseEvent<HTMLButtonElement>,
    disasterStatus: DisasterDetailStatusEntry[]
  ) => {
    e.stopPropagation();
    setSsConfirmDisasterStatus(disasterStatus);
    setSsConfirmOpen(true);
  };
  const ssConfirmClose = () => {
    setSsConfirmOpen(false);
  };
  const ConfirmButton = ({
    disasterStatus,
  }: {
    disasterStatus: DisasterDetailStatusEntry[];
  }) => (
      <IconButton
        color="secondary"
        sx={{ pointerEvents: "auto" }}
        onClick={(e) => ssConfirmBtnClick(e, disasterStatus)}
      >
        <QuestionMark />
      </IconButton>
  );

  return (
    <>
      <Box>
        <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
          <TableContainer>
            <Table
              stickyHeader
              aria-label="sticky table"
              sx={{ width: "100%" }}
            >
              <TableHead>
                <TableRow sx={{ overflowX: "auto" }}>
                  {columns.map((column) => (
                    <TableCell
                      key={`${column.label}_${column.id}`}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      sx={{
                        padding: "8px",
                        "@media screen and (max-width:900px)": {
                          display:
                            column.isSmallScreen === true
                              ? "table-cell"
                              : "none",
                        },
                        backgroundColor:
                          column.id === "condition_kind"
                            ? "primary.light"
                            : "#FFF",
                        color:
                          column.id === "condition_kind"
                            ? "#FFF"
                            : "outline.dark",
                        fontWeight: "bold",
                        borderBottom: "2px solid #606060",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {disasterDetails
                  ?.filter(
                    (disasterDetail) =>
                      (disasterDetail.disaster_statuses?.length ?? 0) > 0
                  )
                  .map((disasterDetail, index) => {
                    return (
                      <TableRow
                        name={`disasterDataManual_${index}`}
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={`${disasterDetail.id}_${index}`}
                        onClick={() => handleLocationCodeClick(disasterDetail)}
                        sx={{
                          pointerEvents: "none",
                          "@media screen and (max-width:900px)": {
                            cursor: "pointer",
                            pointerEvents: "auto",
                          },
                        }}
                      >
                        {columns.map((column) => {
                          return (
                            <TableCell
                              key={`${column.label}_${column.id}`}
                              align={column.align}
                              sx={{
                                padding: "8px",
                                "@media screen and (max-width:900px)": {
                                  display:
                                    column.isSmallScreen === true
                                      ? "table-cell"
                                      : "none",
                                },
                              }}
                            >
                              {column.checkBox ? (
                                <CheckboxValidate
                                  siteId={disasterDetail?.id}
                                  name={
                                    disasterDetail?.description +
                                    " " +
                                    formatDate(disasterDetail?.last_detect_at)
                                  }
                                  sites={disasterDetail.disaster_statuses ?? []}
                                />
                              ) : null}
                              {column.count ? (
                                <>
                                  {disasterDetail.disaster_statuses?.length ?? 0}
                                  <ConfirmButton
                                    disasterStatus={
                                      disasterDetail.disaster_statuses ?? []
                                    }
                                  />
                                </>
                              ) : column.kind ? (
                                disasterValidate(
                                  disasterDetail.condition_kind
                                )
                              ) : column.date ? (
                                formatDate(disasterDetail.last_detect_at)
                              ) : (
                                <>{disasterDetail[column.id as keyof DisasterDetailItem] as string}</>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          {manualCreateDisasterRole.update ? (
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                padding: "8px",
              }}
            >
              <JoinDisasterSelect
                changedValue={(value) => handleChangeDisaster(value)}
              />
              <Button
                name="joinButton"
                variant="contained"
                onClick={joinButtonClick}
                disabled={
                  checkedDetails.length === 0 ||
                  selectedContents.dst_id === null
                }
              >
                結合
              </Button>
              <Button
                name="splitButton"
                variant="outlined"
                onClick={splitButtonClick}
                disabled={checkedDetails.length === 0}
              >
                分割
              </Button>
              <IconButton
                name="deleteButton"
                color="error"
                onClick={deleteButtonClick}
                disabled={checkedDetails.length === 0}
              >
                <DeleteForeverOutlinedIcon />
              </IconButton>
            </Box>
          ) : null}
          <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
            <Box
              sx={{
                display: "flex",
                height: "56px",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
                borderBottom: "2px solid #CCC",
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  paddingLeft: "8px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                災害情報詳細
              </Box>
              <IconButton
                aria-label="close"
                onClick={handlePopoverClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
              <TableBody>
                {selectedRow &&
                  columns.slice(1, 10).map((column) => {
                    // const value = selectedRow[column.id];
                    return (
                      <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                        <TableCell
                          align={column.align}
                          sx={{
                            color: "outline.dark",
                            fontWeight: "bold",
                            boxSizing: "border-box",
                            width: "calc((100vw - 64px) / 2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              WebkitWritingMode: "horizontal-tb",
                              height: "100%",
                            }}
                          >
                            {column.label}
                          </Box>
                        </TableCell>
                        <TableCell
                          align={column.align}
                          sx={{
                            writingMode: "horizontal-tb",
                            boxSizing: "border-box",
                            width: "calc((100% - 64px) / 2)",
                            overflowWrap: "break-word",
                          }}
                        >
                          {column.checkBox && selectedRow ? (
                            <CheckboxValidate
                              siteId={selectedRow.id}
                              sites={selectedRow.disaster_statuses ?? []}
                              name={`${selectedRow.description ?? ""} ${formatDate(selectedRow.last_detect_at)}`}
                            />
                          ) : null}
                          {column.count && selectedRow ? (
                            <>{selectedRow.disaster_statuses?.length ?? 0}</>
                          ) : column.kind && selectedRow ? (
                            disasterValidate(selectedRow.condition_kind)
                          ) : column.date && selectedRow ? (
                            formatDate(selectedRow.last_detect_at)
                          ) : selectedRow ? (
                            <>{String(selectedRow[column.id as keyof DisasterDetailItem] ?? "")}</>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <Box
              sx={{
                margin: "16px",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  padding: "0 8px",
                  height: "36.5px",
                  border: "solid 1px #0752A3",
                  borderRadius: "4px",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  lineHeight: "1.75",
                  letterSpacing: "0.02857",
                  color: "#0752A3",
                }}
              >
                選択
                {selectedRow ? (
                  <CheckboxValidate
                    siteId={selectedRow.id}
                    sites={selectedRow.disaster_statuses ?? []}
                  />
                ) : null}
              </Box>
            </Box>
          </Dialog>
          <PopoverCustom
            open={popoverJoinOpen}
            onClose={joinPopupClose}
            yesNo={joinDisasters}
            title={"結合確認"}
            contents={joinMessage}
          />
          <PopoverCustom
            open={popoverSplitOpen}
            onClose={splitPopupClose}
            yesNo={splitDisasters}
            title={"分割確認"}
            contents={splitMessage}
          />
          <PopoverCustom
            open={popoverDeleteOpen}
            onClose={deletePopupClose}
            yesNo={deleteDisasters}
            title={"削除確認"}
            contents={deleteMessage}
          />
          <PopoverCustom
            open={ssConfirmOpen}
            onClose={ssConfirmClose}
            title={"被災SS一覧"}
            contents={ssConfirmMessage}
          />
          <SnackbarCustom
            data={snackBarstatus}
            openStatus={snackbarOpen}
            onClose={() => setSnackbarOpen(false)}
          />
          <TablePagination
            rowsPerPageOptions={[10, 30, 50, 100]}
            component="div"
            count={totalData}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
              {
                margin: "0 !important",
                padding: "0 !important",
              },
            }}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`}
          />
        </Paper>
      </Box>
    </>
  );
};
export default DisasterInfomation;
